import type { LegacyMatch } from "../api/legacyTypes";

function cleanLabel(value: string) {
  return value.replace(/\bmatches\b/gi, "").replace(/\s+/g, " ").trim();
}

function stageLabel(match: LegacyMatch) {
  const context = `${match.bracket} ${match.group} ${match.round}`.toLowerCase();
  if (/qualif/.test(context)) return "Qualifier";
  if (/group|swiss/.test(context)) return "Group stage";
  if (/playoff|bracket|quarter|semi|final|round of|third place/.test(context)) return "Playoffs";
  return cleanLabel(match.bracket) || "Tournament";
}

interface ContextRound {
  name: string;
  sortOrder: number;
  matches: LegacyMatch[];
}

interface ContextLane {
  key: "main" | "upper" | "lower" | "final";
  label: string;
  rounds: ContextRound[];
}

interface PositionedRound extends ContextRound {
  x: number;
  nodes: Array<{ match: LegacyMatch; x: number; y: number }>;
}

function bracketRounds(match: LegacyMatch, allMatches: LegacyMatch[]): ContextRound[] {
  const sameBracket = allMatches.filter((candidate) => candidate.bracket === match.bracket);
  const rounds = new Map<string, ContextRound>();
  sameBracket.forEach((candidate) => {
    const name = cleanLabel(candidate.round) || "Match";
    const existing = rounds.get(name);
    if (existing) {
      existing.matches.push(candidate);
      existing.sortOrder = Math.min(existing.sortOrder, candidate.sortOrder);
    } else {
      rounds.set(name, { name, sortOrder: candidate.sortOrder, matches: [candidate] });
    }
  });
  return [...rounds.values()]
    .map((round) => ({ ...round, matches: [...round.matches].sort((left, right) => left.sortOrder - right.sortOrder) }))
    .sort((left, right) => left.sortOrder - right.sortOrder);
}

function laneForRound(round: ContextRound): ContextLane["key"] {
  const name = round.name.toLowerCase();
  if (name.includes("grand final")) return "final";
  if (/\blower\b|\blb\b/.test(name)) return "lower";
  if (/\bupper\b|\bub\b/.test(name)) return "upper";
  return "main";
}

function contextLanes(rounds: ContextRound[], multiParticipant: boolean): ContextLane[] {
  const definitions: Array<[ContextLane["key"], string]> = [["main", "Bracket"], ["upper", "Upper"], ["lower", "Lower"], ["final", "Final"]];
  if (multiParticipant) return [{ key: "main", label: "Route", rounds }];
  return definitions.map(([key, label]) => ({ key, label, rounds: rounds.filter((round) => laneForRound(round) === key) })).filter((lane) => lane.rounds.length > 0);
}

function shortRoundLabel(value: string) {
  return value
    .replace(/upper bracket/gi, "UB")
    .replace(/lower bracket/gi, "LB")
    .replace(/quarterfinals?/gi, "QF")
    .replace(/semifinals?/gi, "SF")
    .replace(/grand final/gi, "GF")
    .replace(/round\s+/gi, "R")
    .replace(/matches/gi, "")
    .replace(/\s+/g, " ")
    .trim();
}

function positionLane(lane: ContextLane, top: number, height: number, startX: number, endX: number): PositionedRound[] {
  const denominator = Math.max(lane.rounds.length - 1, 1);
  return lane.rounds.map((round, roundIndex) => {
    const x = lane.rounds.length === 1 ? (startX + endX) / 2 : startX + ((endX - startX) * roundIndex) / denominator;
    const spacing = height / Math.max(round.matches.length, 1);
    return {
      ...round,
      x,
      nodes: round.matches.map((candidate, matchIndex) => ({ match: candidate, x, y: top + spacing * (matchIndex + .5) })),
    };
  });
}

function matchCode(match: LegacyMatch, fallback: number) {
  return match.sourceKey.match(/r\d+m\d+/i)?.[0].toUpperCase() ?? `M${fallback + 1}`;
}

function MultiParticipantBracketMap({ match, rounds }: { match: LegacyMatch; rounds: ContextRound[] }) {
  const opening = rounds.find((round) => /opening/i.test(round.name));
  const upper = rounds.find((round) => /\bupper\b|\bub\b/i.test(round.name));
  const lower = rounds.find((round) => /\blower\b|\blb\b/i.test(round.name));
  const decider = rounds.find((round) => /decider/i.test(round.name));
  const pathCount = Math.max(decider?.matches.length ?? 0, upper?.matches.length ?? 0, lower?.matches.length ?? 0, Math.ceil((opening?.matches.length ?? 0) / 2), 1);
  const height = 34 + pathCount * 94;
  const nodes = [
    ...(opening?.matches ?? []).map((candidate, index) => ({ match: candidate, x: 100, y: 49 + Math.floor(index / 2) * 94 + (index % 2) * 28, code: matchCode(candidate, index) })),
    ...(upper?.matches ?? []).map((candidate, index) => ({ match: candidate, x: 302, y: 47 + index * 94, code: matchCode(candidate, index) })),
    ...(lower?.matches ?? []).map((candidate, index) => ({ match: candidate, x: 302, y: 79 + index * 94, code: matchCode(candidate, index) })),
    ...(decider?.matches ?? []).map((candidate, index) => ({ match: candidate, x: 520, y: 63 + index * 94, code: matchCode(candidate, index) })),
  ];
  const currentNode = nodes.find((node) => node.match.sourceKey === match.sourceKey);
  const connectors: Array<{ key: string; source: typeof nodes[number]; target: typeof nodes[number] }> = [];
  for (let pathIndex = 0; pathIndex < pathCount; pathIndex += 1) {
    const openingNodes = nodes.filter((node) => opening?.matches.includes(node.match)).slice(pathIndex * 2, pathIndex * 2 + 2);
    const upperNode = nodes.find((node) => node.match === upper?.matches[pathIndex]);
    const lowerNode = nodes.find((node) => node.match === lower?.matches[pathIndex]);
    const deciderNode = nodes.find((node) => node.match === decider?.matches[pathIndex]);
    openingNodes.forEach((source) => {
      if (upperNode) connectors.push({ key: `${source.match.sourceKey}-upper`, source, target: upperNode });
      if (lowerNode) connectors.push({ key: `${source.match.sourceKey}-lower`, source, target: lowerNode });
    });
    if (upperNode && deciderNode) connectors.push({ key: `${upperNode.match.sourceKey}-decider`, source: upperNode, target: deciderNode });
    if (lowerNode && deciderNode) connectors.push({ key: `${lowerNode.match.sourceKey}-decider`, source: lowerNode, target: deciderNode });
  }

  return <svg className="mini-bracket-map multi-participant-map" viewBox={`0 0 600 ${height}`} role="img" aria-label={`Bracket overview highlighting ${cleanLabel(match.round) || "this match"}`}>
    <text className="mini-map-column-label" x="100" y="17">OPENING HEATS</text>
    <text className="mini-map-column-label" x="302" y="17">UPPER / LOWER HEATS</text>
    <text className="mini-map-column-label" x="520" y="17">DECIDER</text>
    {Array.from({ length: pathCount }, (_, index) => <g className="mini-map-path-label" key={`path-${index}`}>
      {index > 0 && <line x1="14" x2="586" y1={28 + index * 94} y2={28 + index * 94} />}
      <text x="10" y={39 + index * 94}>PATH {index + 1}</text>
    </g>)}
    {connectors.map(({ key, source, target }) => {
      const highlighted = source.match.sourceKey === match.sourceKey || target.match.sourceKey === match.sourceKey;
      const midX = (source.x + target.x) / 2;
      return <path className={`mini-map-connector ${highlighted ? "current-route" : ""}`} d={`M ${source.x + 27} ${source.y} H ${midX} V ${target.y} H ${target.x - 27}`} key={key} />;
    })}
    {rounds.map((round) => <g className="mini-map-round" key={round.name}>
      {nodes.filter((node) => round.matches.includes(node.match)).map((node) => {
        const current = node.match.sourceKey === match.sourceKey;
        return <g className={`mini-map-match-node ${current ? "current-match" : node.match.status === "completed" ? "completed-match" : ""}`} key={node.match.sourceKey}>
          <title>{current ? "This match" : `${cleanLabel(node.match.round)}: ${[node.match.participant1, node.match.participant2, node.match.participant3, node.match.participant4].filter(Boolean).join(", ") || "TBD"}`}</title>
          <rect x={node.x - 26} y={node.y - 9} width="52" height="18" rx="5" />
          <text className="mini-map-node-label" x={node.x} y={node.y + 3}>{node.code}</text>
        </g>;
      })}
    </g>)}
    {currentNode && <g className="mini-map-you-are-here"><path d={`M ${currentNode.x} ${currentNode.y - 11} V ${currentNode.y - 21}`} /><text x={currentNode.x} y={currentNode.y - 25}>THIS MATCH</text></g>}
  </svg>;
}

function MiniBracketMap({ match, rounds }: { match: LegacyMatch; rounds: ContextRound[] }) {
  const multiParticipant = rounds.some((round) => round.matches.some((candidate) => Boolean(candidate.participant3 || candidate.participant4)));
  const trackmaniaGroupRoute = rounds.some((round) => /opening/i.test(round.name))
    && rounds.some((round) => /\bupper\b|\bub\b|\blower\b|\blb\b/i.test(round.name))
    && rounds.some((round) => /decider/i.test(round.name));
  if (multiParticipant && trackmaniaGroupRoute) return <MultiParticipantBracketMap match={match} rounds={rounds} />;
  const lanes = contextLanes(rounds, multiParticipant);
  const splitBracket = lanes.some((lane) => lane.key === "upper") && lanes.some((lane) => lane.key === "lower");
  const finalLane = lanes.find((lane) => lane.key === "final");
  const drawableLanes = lanes.filter((lane) => lane.key !== "final");
  const height = splitBracket ? 220 : 145;
  const positions = drawableLanes.map((lane, laneIndex) => {
    const laneTop = splitBracket ? (lane.key === "lower" ? 126 : 28) : 30 + laneIndex * 86;
    const laneHeight = splitBracket ? 66 : Math.min(88, height - laneTop - 12);
    return { lane, rounds: positionLane(lane, laneTop, laneHeight, 66, finalLane ? 455 : 552) };
  });
  const finalRound = finalLane ? positionLane(finalLane, splitBracket ? 94 : 30, splitBracket ? 38 : 80, 548, 548)[0] : undefined;
  const allPositioned = positions.flatMap((lane) => lane.rounds).concat(finalRound ? [finalRound] : []);
  const currentNode = allPositioned.flatMap((round) => round.nodes).find((node) => node.match.sourceKey === match.sourceKey);

  return <svg className="mini-bracket-map" viewBox={`0 0 600 ${height}`} role="img" aria-label={`Bracket overview highlighting ${cleanLabel(match.round) || "this match"}`}>
    {positions.map(({ lane, rounds: laneRounds }) => <g className={`mini-map-lane mini-map-${lane.key}`} key={lane.key}>
      <text className="mini-map-lane-label" x="4" y={(laneRounds[0]?.nodes[0]?.y ?? 30) - 12}>{lane.label}</text>
      {laneRounds.slice(0, -1).flatMap((round, roundIndex) => {
        const target = laneRounds[roundIndex + 1];
        return round.nodes.map((node, nodeIndex) => {
          const targetIndex = Math.min(target.nodes.length - 1, Math.floor(nodeIndex * target.nodes.length / Math.max(round.nodes.length, 1)));
          const targetNode = target.nodes[targetIndex];
          const highlighted = node.match.sourceKey === match.sourceKey || targetNode.match.sourceKey === match.sourceKey;
          const midX = (node.x + targetNode.x) / 2;
          return <path className={`mini-map-connector ${highlighted ? "current-route" : ""}`} d={`M ${node.x + 10} ${node.y} H ${midX} V ${targetNode.y} H ${targetNode.x - 10}`} key={`${round.name}-${node.match.sourceKey}`} />;
        });
      })}
    </g>)}
    {finalRound && positions.flatMap(({ rounds: laneRounds }) => {
      const source = laneRounds.at(-1)?.nodes[0];
      const target = finalRound.nodes[0];
      if (!source || !target) return [];
      const highlighted = source.match.sourceKey === match.sourceKey || target.match.sourceKey === match.sourceKey;
      return [<path className={`mini-map-connector ${highlighted ? "current-route" : ""}`} d={`M ${source.x + 10} ${source.y} H 500 V ${target.y} H ${target.x - 10}`} key={`final-${source.match.sourceKey}`} />];
    })}
    {allPositioned.map((round) => <g className="mini-map-round" key={`${round.name}-${round.x}`}>
      <text x={round.x} y={Math.max(13, Math.min(...round.nodes.map((node) => node.y)) - 10)}>{shortRoundLabel(round.name)}</text>
      {round.nodes.map((node) => {
        const current = node.match.sourceKey === match.sourceKey;
        return <g className={current ? "current-match" : node.match.status === "completed" ? "completed-match" : ""} key={node.match.sourceKey}>
          <title>{current ? "This match" : `${cleanLabel(node.match.round)}: ${[node.match.participant1, node.match.participant2].filter(Boolean).join(" vs ") || "TBD"}`}</title>
          <rect x={node.x - (current ? 13 : 9)} y={node.y - (current ? 6 : 4)} width={current ? 26 : 18} height={current ? 12 : 8} rx="4" />
        </g>;
      })}
    </g>)}
    {currentNode && <g className="mini-map-you-are-here"><path d={`M ${currentNode.x} ${currentNode.y - 8} V ${currentNode.y - 17}`} /><text x={currentNode.x} y={currentNode.y - 21}>THIS MATCH</text></g>}
  </svg>;
}

export function MatchContext({ match, allMatches }: { match: LegacyMatch; allMatches: LegacyMatch[] }) {
  const stage = stageLabel(match);
  const bracket = cleanLabel(match.bracket);
  const round = cleanLabel(match.round);
  const detailTags = [stage];
  if (bracket && bracket.toLowerCase() !== stage.toLowerCase() && !/^playoffs?$/i.test(bracket)) detailTags.push(bracket);
  if (round && !detailTags.some((tag) => tag.toLowerCase() === round.toLowerCase())) detailTags.push(round);
  const rounds = bracketRounds(match, allMatches);

  return <span className="match-context">
    <button type="button" className="match-context-trigger" aria-label={`Show position of ${round || "match"} in ${bracket || stage}`}>
      {detailTags.map((tag, index) => <span className={index === 0 ? "match-stage-tag" : "match-detail-tag"} key={`${tag}-${index}`}>{tag}</span>)}
    </button>
    <aside className="match-context-popover" role="tooltip">
      <header><div><small>Match position</small><strong>{bracket || stage}</strong></div><span>Bracket overview</span></header>
      <MiniBracketMap match={match} rounds={rounds} />
    </aside>
  </span>;
}
